import React, { useState, useEffect } from 'react';
import { X, MapPin, Calendar, DollarSign, Users, Clock, Mail, MessageCircle, Edit } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { collaborationsService } from '../services/collaborationsService';
import { ArtCategory, Collaboration } from '../types';
import EditCollaborationModal from './EditCollaborationModal';

const statusColors = {
  open: 'bg-green-100 text-green-800',
  in_progress: 'bg-blue-100 text-blue-800',
  completed: 'bg-gray-100 text-gray-800',
  cancelled: 'bg-red-100 text-red-800',
};

const applicationStatusColors = {
  pending: 'bg-yellow-100 text-yellow-800',
  accepted: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
};

interface CollaborationDetailModalProps {
  collaboration: Collaboration | Record<string, unknown>;
  isOpen: boolean;
  onClose: () => void;
  onApply?: (collaborationId: string, message: string) => void;
  onUpdateApplication?: (applicationId: string, status: 'accepted' | 'rejected') => void;
  onUpdate?: (updated: Collaboration) => void;
}

const CollaborationDetailModal: React.FC<CollaborationDetailModalProps> = ({
  collaboration,
  isOpen,
  onClose,
  onApply,
  onUpdateApplication,
  onUpdate,
}) => {
  const { user } = useAuth();
  const { select, t, locale } = useLanguage();
  const categoryLabels = React.useMemo(
    () => ({
      music: t('categories.music', select({ es: 'Música', en: 'Music' })),
      writing: t('categories.writing', select({ es: 'Escritura', en: 'Writing' })),
      tattoo: t('categories.tattoo', select({ es: 'Tatuajes', en: 'Tattoos' })),
      digital_design: t('categories.digital_design', select({ es: 'Diseño Digital', en: 'Digital Design' })),
      fashion_design: t('categories.fashion_design', select({ es: 'Diseño de Moda', en: 'Fashion Design' })),
      photography: t('categories.photography', select({ es: 'Fotografía', en: 'Photography' })),
      sculpture: t('categories.sculpture', select({ es: 'Escultura', en: 'Sculpture' })),
      painting: t('categories.painting', select({ es: 'Pintura', en: 'Painting' })),
      dance: t('categories.dance', select({ es: 'Danza', en: 'Dance' })),
      filmmaking: t('categories.filmmaking', select({ es: 'Cine', en: 'Filmmaking' })),
      makeup: t('categories.makeup', select({ es: 'Maquillaje', en: 'Makeup' })),
    }),
    [select, t]
  );

  const statusLabels = React.useMemo(
    () => ({
      open: select({ es: 'Abierta', en: 'Open' }),
      in_progress: select({ es: 'En progreso', en: 'In progress' }),
      completed: select({ es: 'Completada', en: 'Completed' }),
      cancelled: select({ es: 'Cancelada', en: 'Cancelled' }),
    }),
    [select]
  );

  const applicationStatusLabels = React.useMemo(
    () => ({
      pending: select({ es: 'Pendiente', en: 'Pending' }),
      accepted: select({ es: 'Aceptada', en: 'Accepted' }),
      rejected: select({ es: 'Rechazada', en: 'Rejected' }),
    }),
    [select]
  );
  const [showApplicationForm, setShowApplicationForm] = useState(false);
  const [applicationMessage, setApplicationMessage] = useState('');
  const [applyError, setApplyError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [applications, setApplications] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [currentCollaboration, setCurrentCollaboration] = useState(collaboration);

  useEffect(() => {
    setCurrentCollaboration(collaboration);
  }, [collaboration]);

  useEffect(() => {
    if (isOpen && currentCollaboration) {
      void loadApplications();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, currentCollaboration]);

  const loadApplications = async () => {
    if (!currentCollaboration?.id) return;
    
    setLoading(true);
    try {
      const collab = currentCollaboration as Record<string, unknown>;
      const apps = Array.isArray(collab.collaboration_applications)
        ? (collab.collaboration_applications as Record<string, unknown>[])
        : [];
      setApplications(apps);
    } catch (err: unknown) {
      console.error('Error loading applications:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!applicationMessage.trim() || !currentCollaboration?.id) return;

    setSubmitting(true);
    setApplyError('');
    try {
      if (onApply) {
        await onApply(currentCollaboration.id, applicationMessage.trim());
        setShowApplicationForm(false);
        setApplicationMessage('');
        // Reload applications
        await loadApplications();
      }
    } catch (err) {
      console.error('Error applying to collaboration:', err);
      setApplyError(select({
        es: 'No se pudo enviar la aplicación. Revisa el contenido e inténtalo de nuevo.',
        en: 'Could not submit the application. Review the content and try again.',
      }));
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateApplicationStatus = async (applicationId: string, status: 'accepted' | 'rejected') => {
    try {
      if (onUpdateApplication) {
        await collaborationsService.updateApplicationStatus(applicationId, status);
        onUpdateApplication(applicationId, status);
        // Reload applications
        await loadApplications();
      }
    } catch (err) {
      console.error('Error updating application status:', err);
      alert(
        select({ es: 'Error al actualizar el estado: ', en: 'Error updating status: ' }) +
        (err instanceof Error ? err.message : select({ es: 'Error desconocido', en: 'Unknown error' }))
      );
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'EUR',
    }).format(price);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (!isOpen || !currentCollaboration) return null;

  const isOwner = user?.id === currentCollaboration.profiles?.id || user?.id === currentCollaboration.author_id;
  const hasApplied = applications.some(app => app.applicant_id === user?.id);
  const isOpen_status = currentCollaboration.status === 'open';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[9999]">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <div className="flex h-full max-h-[90vh]">
          {/* Main Content */}
          <div className="flex-1 overflow-y-auto">
            <div className="p-6">
              {/* Header */}
              <div className="flex items-start justify-between mb-6">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-4">
                    <h1 className="text-2xl font-bold text-gray-900">{currentCollaboration.title}</h1>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusColors[currentCollaboration.status as keyof typeof statusColors]}`}>
                      {statusLabels[currentCollaboration.status as keyof typeof statusLabels]}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-4 text-sm text-gray-600 mb-4">
                    <div className="flex items-center gap-2">
                      <img
                        src={currentCollaboration.profiles?.avatar_url || 'https://images.pexels.com/photos/1181519/pexels-photo-1181519.jpeg?auto=compress&cs=tinysrgb&w=24&h=24&fit=crop'}
                        alt={currentCollaboration.profiles?.full_name}
                        className="w-6 h-6 rounded-full object-cover"
                      />
                      <span className="font-medium">{currentCollaboration.profiles?.full_name}</span>
                      {currentCollaboration.profiles?.is_premium && <span className="text-yellow-500">✨</span>}
                    </div>
                    
                    <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                      {categoryLabels[currentCollaboration.category as ArtCategory]}
                    </span>
                    
                    <div className="flex items-center gap-1">
                      <Clock size={14} />
                      <span>
                        {select({ es: 'Publicado', en: 'Posted' })} {formatDate(currentCollaboration.created_at)}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  {isOwner && (
                    <button
                      onClick={() => setShowEditModal(true)}
                      className="p-2 hover:bg-gray-100 rounded-full transition-colors text-purple-600"
                      title={select({ es: 'Editar colaboración', en: 'Edit collaboration' })}
                    >
                      <Edit size={24} />
                    </button>
                  )}
                  <button
                    onClick={onClose}
                    className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                  >
                    <X size={24} />
                  </button>
                </div>
              </div>

              {/* Details Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                {currentCollaboration.location && (
                  <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                    <MapPin className="text-gray-400" size={16} />
                    <div>
                      <p className="text-xs text-gray-500">{select({ es: 'Ubicación', en: 'Location' })}</p>
                      <p className="font-medium text-gray-900">{currentCollaboration.location}</p>
                    </div>
                  </div>
                )}
                
                {currentCollaboration.budget && (
                  <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                    <DollarSign className="text-gray-400" size={16} />
                    <div>
                      <p className="text-xs text-gray-500">{select({ es: 'Presupuesto', en: 'Budget' })}</p>
                      <p className="font-medium text-gray-900">{formatPrice(currentCollaboration.budget)}</p>
                    </div>
                  </div>
                )}
                
                {currentCollaboration.deadline && (
                  <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                    <Calendar className="text-gray-400" size={16} />
                    <div>
                      <p className="text-xs text-gray-500">{select({ es: 'Fecha límite', en: 'Deadline' })}</p>
                      <p className="font-medium text-gray-900">{formatDate(currentCollaboration.deadline)}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Description */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">{select({ es: 'Descripción del proyecto', en: 'Project description' })}</h3>
                <div className="prose prose-sm max-w-none">
                  <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">{currentCollaboration.description}</p>
                </div>
              </div>

              {/* Requirements */}
              {currentCollaboration.requirements && currentCollaboration.requirements.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">{select({ es: 'Requisitos', en: 'Requirements' })}</h3>
                  <div className="space-y-2">
                    {currentCollaboration.requirements.map((req: string, index: number) => (
                      <div key={index} className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-purple-600 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-gray-700">{req}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Application Section */}
              {!isOwner && isOpen_status && (
                <div className="mb-6 p-4 border border-gray-200 rounded-lg">
                  {hasApplied ? (
                    <div className="text-center">
                      <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <MessageCircle className="text-blue-600" size={24} />
                      </div>
                      <h3 className="font-medium text-gray-900 mb-2">{select({ es: 'Ya has aplicado', en: 'You have already applied' })}</h3>
                      <p className="text-sm text-gray-600">{select({ es: 'Tu aplicación está siendo revisada por el creador del proyecto.', en: 'Your application is being reviewed by the project owner.' })}</p>
                    </div>
                  ) : showApplicationForm ? (
                    <form onSubmit={handleApply} className="space-y-4">
                      <h3 className="font-medium text-gray-900">{select({ es: 'Aplicar a esta colaboración', en: 'Apply to this collaboration' })}</h3>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {select({ es: 'Mensaje de aplicación', en: 'Application message' })}
                        </label>
                        <textarea
                          required
                          rows={4}
                          value={applicationMessage}
                          onChange={(e) => setApplicationMessage(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                          placeholder={select({ es: 'Explica por qué eres el candidato ideal para esta colaboración...', en: 'Explain why you are the ideal candidate for this collaboration...' })}
                        />
                      </div>
                      {applyError && (
                        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                          {applyError}
                        </div>
                      )}
                      <div className="flex gap-3">
                        <button
                          type="button"
                          onClick={() => {
                            setShowApplicationForm(false);
                            setApplicationMessage('');
                            setApplyError('');
                          }}
                          className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          {select({ es: 'Cancelar', en: 'Cancel' })}
                        </button>
                        <button
                          type="submit"
                          disabled={submitting || !applicationMessage.trim()}
                          className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
                        >
                          {submitting
                            ? select({ es: 'Enviando...', en: 'Sending...' })
                            : select({ es: 'Enviar aplicación', en: 'Send application' })}
                        </button>
                      </div>
                    </form>
                  ) : (
                    <div className="text-center">
                      <h3 className="font-medium text-gray-900 mb-2">{select({ es: '¿Te interesa esta colaboración?', en: 'Interested in this collaboration?' })}</h3>
                      <p className="text-sm text-gray-600 mb-4">{select({ es: 'Envía tu aplicación y cuéntanos por qué eres el candidato ideal.', en: 'Send your application and tell us why you are the ideal candidate.' })}</p>
                      <button
                        onClick={() => {
                          setApplyError('');
                          setApplicationMessage('');
                          setShowApplicationForm(true);
                        }}
                        className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                      >
                        {select({ es: 'Aplicar ahora', en: 'Apply now' })}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Applications Sidebar */}
          <div className="w-80 border-l border-gray-200 overflow-y-auto">
            <div className="p-4">
              <div className="flex items-center gap-2 mb-4">
                <Users className="text-gray-400" size={16} />
                <h3 className="font-semibold text-gray-900">
                  {select({ es: 'Aplicaciones', en: 'Applications' })} ({applications.length})
                </h3>
              </div>

              {loading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="animate-pulse bg-gray-200 h-20 rounded-lg"></div>
                  ))}
                </div>
              ) : applications.length > 0 ? (
                <div className="space-y-3">
                  {applications.map((application: Record<string, unknown>) => {
                    const profiles = (application.profiles ?? {}) as Record<string, unknown>;
                    const applicationId = application.id as string;
                    const applicationStatus = application.status as 'pending' | 'accepted' | 'rejected';
                    return (
                      <div key={applicationId} className="border border-gray-200 rounded-lg p-3">
                        <div className="flex items-start gap-3 mb-3">
                          <img
                            src={(profiles.avatar_url as string) || 'https://images.pexels.com/photos/1181519/pexels-photo-1181519.jpeg?auto=compress&cs=tinysrgb&w=32&h=32&fit=crop'}
                            alt={profiles.full_name as string}
                            className="w-8 h-8 rounded-full object-cover"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-1">
                              <h4 className="font-medium text-gray-900 text-sm truncate">
                                {profiles.full_name as string}
                              </h4>
                              <span className={`px-2 py-1 text-xs rounded-full ${applicationStatusColors[applicationStatus]}`}>
                                {applicationStatusLabels[applicationStatus]}
                              </span>
                            </div>
                            <p className="text-xs text-gray-500">@{profiles.username as string}</p>
                          </div>
                        </div>

                        <p className="text-sm text-gray-700 mb-3 line-clamp-3">{application.message as string}</p>

                        <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
                          <span>{formatDate(application.created_at as string)}</span>
                        </div>

                        {/* Actions for owner */}
                        {isOwner && applicationStatus === 'pending' && (
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleUpdateApplicationStatus(applicationId, 'accepted')}
                            className="flex-1 px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 transition-colors"
                          >
                            {select({ es: 'Aceptar', en: 'Accept' })}
                          </button>
                          <button
                            onClick={() => handleUpdateApplicationStatus(applicationId, 'rejected')}
                            className="flex-1 px-3 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700 transition-colors"
                          >
                            {select({ es: 'Rechazar', en: 'Reject' })}
                          </button>
                        </div>
                      )}

                      {/* Contact button for accepted applications */}
                      {applicationStatus === 'accepted' && (
                        <button className="w-full px-3 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center gap-2">
                          <Mail size={14} />
                          {select({ es: 'Contactar', en: 'Contact' })}
                        </button>
                      )}
                    </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Users size={32} className="mx-auto text-gray-300 mb-3" />
                  <h3 className="font-medium text-gray-900 mb-1">{select({ es: 'Sin aplicaciones', en: 'No applications yet' })}</h3>
                  <p className="text-sm text-gray-500">
                    {isOwner 
                      ? select({ es: 'Aún no hay aplicaciones para esta colaboración', en: 'There are no applications for this collaboration yet' })
                      : select({ es: 'Sé el primero en aplicar', en: 'Be the first to apply' })
                    }
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {showEditModal && (
        <EditCollaborationModal
          isOpen={showEditModal}
          collaboration={currentCollaboration}
          onClose={() => setShowEditModal(false)}
          onSaved={(updated) => {
            setCurrentCollaboration(updated);
            if (onUpdate) onUpdate(updated);
          }}
        />
      )}
    </div>
  );
};

export default CollaborationDetailModal;
